//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  AssigneeSelect,
  EditableTextCell,
  TableStatus,
  OrdersRowExpand,
  DateTimeDisplay,
  OrdersProgressBar,
  PolarisIcon,
  OrdersOverflowMenu
} from '@tmt-insights/oms_fe_core/columns';
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { Order, OrderDetail, OrderJob } from '@/redux/slices/slice_types';
import { Typography, Box } from '@tmt-insights/oms_fe_core/material';
import { getLineageMetaTitle } from '@tmt-insights/oms_fe_core/utils';
import { Grid } from '@mui/material';

const columns: AllTableColumnsConfig<Order & OrderDetail> = {
  expandable: {
    label: '',
    isExpandable: true,
    disableExpand: (row: OrderDetail) => row.status === 'draft' || row.order_type === 'bulk',
    render: (props: { item: any; isCollapsed: boolean; hasCategories: boolean }) => {
      const extraColumns = [
        {
          label: 'Title',
          width: 300,
          render: (job: OrderJob) => (
            <>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {job.title.lineage_meta?.series_name}
              </Typography>
              <Typography variant="caption">{getLineageMetaTitle(job.title)}</Typography>
              {job.version?.name && (
                <>
                  <br />
                  <Typography variant="caption" sx={{ color: '#667799' }}>
                    {job.version?.name}
                  </Typography>
                </>
              )}
            </>
          )
        },
        {
          label: 'Program ID',
          minWidth: 70,
          render: (job: OrderJob) => job.version?.program_id || '-'
        },
        {
          label: 'Piece ID',
          minWidth: 70,
          render: (job: OrderJob) => job.version?.piece_id || '-'
        },
        {
          label: 'Piece Number',
          minWidth: 150,
          render: (job: OrderJob) => job.version?.piece_number || '-'
        }
      ];
      return <OrdersRowExpand extraColumns={extraColumns} {...props} />;
    }
  },
  hid: {
    label: 'Order ID',
    clickCopy: true,
    sortField: 'id',
    minWidth: 110,
    render: (cell: OrderDetail['hid']) => <strong>{cell || '-'}</strong>
  },
  description: {
    label: 'Description',
    clickCopy: true,
    render: (cell: OrderDetail['description'], row: OrderDetail) => {
      return (
        <Grid container alignItems="center" flexWrap="nowrap">
          {row.order_type === 'bulk' && (
            <Box sx={{ color: 'secondary.main', mr: 1, height: 20, width: 20, float: 'left' }}>
              <PolarisIcon icon="bulk" height={20} />
            </Box>
          )}
          <EditableTextCell value={cell} row={row} update="order" field="description" emptyText="-" />
        </Grid>
      );
    },
    minWidth: 300,
    required: true
  },
  po_number: {
    label: 'Order Number',
    minWidth: 210,
    render: (cell: OrderDetail['po_number'], row: OrderDetail) => (
      <EditableTextCell required={false} value={cell} row={row} update="order" label="Order Number" field="po_number" emptyText="-" />
    )
  },
  managed_by_user: {
    label: 'Managed by',
    minWidth: 220,
    disableSort: true,
    render: (cell: OrderDetail['managed_by_user'], row: OrderDetail) => (
      <AssigneeSelect value={cell} id={row.id} row={row} update="order" assigns="user" emptyText="-" />
    )
  },
  billable_customer: {
    label: 'Client Name'
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    minWidth: 100,
    render: (cell: OrderDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  status: {
    label: 'Status',
    render: (cell: OrderDetail['status']) => <TableStatus status={cell} />
  },
  jobs: {
    label: 'Progress',
    minWidth: 100,
    disableSort: true,
    render: (cell: OrderDetail['jobs'], row: Order) => <OrdersProgressBar jobs={cell} order={row} />
  },
  overflow: {
    label: '',
    isOverflow: true,
    width: 50,
    render: (_cell: any, row: Order) => <OrdersOverflowMenu order={row} />
  }
};

export default columns;
